import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;

function fmtEur(centimes) {
  return ((centimes || 0) / 100).toFixed(2) + " €";
}

const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

export default function CommissionsPanel({ token, toast }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/api/admin/affiliates/commissions/${year}/${month}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(d);
    } catch {
      toast.error("Erreur chargement commissions");
    } finally {
      setLoading(false);
    }
  }, [year, month, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleMarkPaid = async (affiliateId, paymentIds) => {
    try {
      await axios.post(
        `${API}/api/admin/affiliates/${affiliateId}/mark-paid`,
        { paymentIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Commissions marquées comme payées ✅");
      fetchData();
    } catch {
      toast.error("Erreur");
    }
  };

  const totalCommission = data?.affiliates?.reduce((s, a) => s + a.commission, 0) || 0;
  const totalUnpaid = data?.affiliates?.filter(a => !a.allPaid).reduce((s, a) => s + a.commission, 0) || 0;

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center flex-wrap">
        <select
          value={month}
          onChange={(e) => setMonth(Number(e.target.value))}
          className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm text-white"
        >
          {MONTHS.map((m, i) => (
            <option key={i + 1} value={i + 1}>{m}</option>
          ))}
        </select>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm text-white"
        >
          {[2025, 2026, 2027, 2028].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="text-gray-400 text-sm">Chargement…</p>
      ) : !data?.affiliates?.length ? (
        <p className="text-gray-500 italic text-sm">Aucune vente affiliée pour cette période.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
              <p className="text-xs text-emerald-300 uppercase tracking-wider">Total commissions du mois</p>
              <p className="text-3xl font-bold text-emerald-200 mt-1">{fmtEur(totalCommission)}</p>
              <p className="text-xs text-emerald-400/70 mt-1">{data.affiliates.length} affilié(s) ont vendu ce mois</p>
            </div>
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
              <p className="text-xs text-amber-300 uppercase tracking-wider">Reste à verser</p>
              <p className="text-3xl font-bold text-amber-200 mt-1">{fmtEur(totalUnpaid)}</p>
              <p className="text-xs text-amber-400/70 mt-1">{data.affiliates.filter(a => !a.allPaid).length} affilié(s) à régler</p>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="min-w-full text-sm">
              <thead className="bg-white/5 text-gray-400 uppercase text-xs">
                <tr>
                  <th className="py-3 px-4 text-left">Affilié</th>
                  <th className="py-3 px-4 text-left">Code</th>
                  <th className="py-3 px-4 text-right">Ventes</th>
                  <th className="py-3 px-4 text-right">CA</th>
                  <th className="py-3 px-4 text-right">Taux</th>
                  <th className="py-3 px-4 text-right">Commission</th>
                  <th className="py-3 px-4 text-left">Statut</th>
                  <th className="py-3 px-4 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.affiliates.map((row) => (
                  <tr key={row.affiliate.id} className="border-t border-white/5 hover:bg-white/5">
                    <td className="py-3 px-4 font-medium text-white">{row.affiliate.name}</td>
                    <td className="py-3 px-4 font-mono text-indigo-300 text-xs">{row.affiliate.couponCode}</td>
                    <td className="py-3 px-4 text-right text-gray-300">{row.salesCount}</td>
                    <td className="py-3 px-4 text-right text-gray-300">{fmtEur(row.totalAmount)}</td>
                    <td className="py-3 px-4 text-right text-amber-300">{(row.affiliate.commissionRate * 100).toFixed(0)}%</td>
                    <td className="py-3 px-4 text-right font-semibold text-emerald-300">{fmtEur(row.commission)}</td>
                    <td className="py-3 px-4">
                      {row.allPaid ? (
                        <span className="text-xs text-emerald-400">✅ Payé</span>
                      ) : (
                        <span className="text-xs text-amber-400">À verser</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {!row.allPaid && (
                        <button
                          onClick={() => handleMarkPaid(row.affiliate.id, row.paymentIds)}
                          className="px-2 py-1 bg-emerald-600/30 hover:bg-emerald-600/50 border border-emerald-500/30 rounded text-xs text-emerald-300"
                        >
                          Marquer payé
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}